<!--
  Analysis block embedded under the KPI table (/web/kpi).

  Reconstructs, month by month since 2020, the m3 that the quotation dashboard
  (/dashboard/analytics) would have shown at each point in time:
    IN  = quotation row creation date (createdAt)
    OUT = creation date of the FINAL invoice (balance/solde or direct invoice)
    An ADVANCE invoice never removes a quotation from the dashboard.

  Purpose: show management where today's m3 sits against six years of history,
  and expose the structural low seasons.

  Color: years are an ORDINAL scale (old -> recent), so they use one hue
  light -> dark rather than seven unrelated hues. Both ramps and the in/out pair
  were validated with the dataviz palette validator against the white card
  surface (all checks pass).
-->
<template>
  <vx-card title="Analysis — Quotation dashboard volume (m³)" class="mb-base w-full">
    <div slot="no-body" class="p-4">

      <!-- Controls -->
      <div class="vx-row items-center mb-4">
        <div class="vx-col w-full md:w-2/3 text-sm qb-muted">
          Approved quotations, from creation to the <strong>final</strong> invoice
          (balance or direct). An advance invoice does not remove a quotation.
        </div>
        <div class="vx-col w-full md:w-1/3 text-right">
          <vs-checkbox v-model="includeParents" class="inline-flex mr-4 text-sm">Include split quotations</vs-checkbox>
          <vs-button size="small" type="border" icon-pack="feather" icon="icon-refresh-cw"
                     :disabled="loading" @click="load(true)">Refresh</vs-button>
        </div>
      </div>

      <!--
        Everything conditional lives inside this single wrapper, and every branch
        carries an explicit key. Without it the parent's children array grows from
        2 to ~25 nodes when `loading` flips, Vue's un-keyed diff pairs the
        <vs-popup> below against a plain div, and destroys it — but Vuesax has
        already relocated that popup into document.body, so the patch dies on a
        null parentNode and Vue falls back to the previous tree, freezing
        "Loading history…" on screen forever.
      -->
      <div class="qb-content">
      <div v-if="loading" key="loading" class="text-center py-8 qb-muted">Loading history…</div>
      <div v-else-if="errorMessage" key="error" class="text-danger text-sm py-4">{{ errorMessage }}</div>

      <div v-else-if="series.length" key="content">

        <!-- Parity badge: if this ever goes red, the page says so itself. -->
        <div class="qb-parity mb-4" :class="meta.parity_ok ? 'qb-parity-ok' : 'qb-parity-ko'">
          <feather-icon :icon="meta.parity_ok ? 'CheckCircleIcon' : 'AlertTriangleIcon'"
                        svgClasses="h-4 w-4 align-middle mr-2" />
          <span v-if="meta.parity_ok">
            Matches the live dashboard ({{ fmt(meta.live_dashboard_m3) }} m³)
          </span>
          <span v-else>
            Does not match the live dashboard — reconstruction {{ fmt(meta.current_backlog_m3) }} m³
            vs dashboard {{ fmt(meta.live_dashboard_m3) }} m³
            (delta {{ fmt(meta.parity_delta_m3) }} m³). Treat the figures below with caution.
          </span>
        </div>

        <!-- KPI tiles -->
        <div class="vx-row mb-4">
          <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-2">
            <div class="qb-hero">
              <div class="qb-hero-value">{{ fmt(meta.current_backlog_m3) }} <span class="qb-unit">m³</span></div>
              <div class="qb-hero-label">In the dashboard today</div>
              <div class="qb-hero-sub">
                {{ fmt(meta.current_backlog_eur) }} € · {{ meta.current_backlog_count }} quotations
                <span v-if="yoyDelta !== null" class="qb-chip"
                      :class="yoyDelta >= 0 ? 'qb-chip-up' : 'qb-chip-down'">
                  {{ yoyDelta >= 0 ? '+' : '' }}{{ yoyDelta }}% vs {{ lastYearLabel }}
                </span>
              </div>
            </div>
          </div>
          <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-2">
            <div class="qb-tile">
              <div class="qb-tile-value">{{ meta.percentile }}<span class="qb-unit">th pct</span></div>
              <div class="qb-tile-label">Historical position</div>
              <div class="qb-tile-sub">{{ percentileWording }}</div>
            </div>
          </div>
          <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-2">
            <div class="qb-tile">
              <div class="qb-tile-value">{{ fmt(allTimeMax.backlog_m3) }} <span class="qb-unit">m³</span></div>
              <div class="qb-tile-label">All-time high — {{ allTimeMax.month }}</div>
              <div class="qb-tile-sub">Low {{ fmt(allTimeMin.backlog_m3) }} m³ — {{ allTimeMin.month }}</div>
            </div>
          </div>
          <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-2">
            <div class="qb-tile">
              <div class="qb-tile-value">{{ num(currentCohort.avg_days_in_dashboard) }} <span class="qb-unit">days</span></div>
              <div class="qb-tile-label">Average time in dashboard</div>
              <div class="qb-tile-sub">
                {{ currentCohort.cohort_year }} cohort ·
                median {{ num(currentCohort.median_days) }} d ·
                previous year {{ num(previousCohort.avg_days_in_dashboard) }} d
              </div>
            </div>
          </div>
        </div>

        <div class="vx-row mb-6">
          <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-2">
            <div class="qb-mini"><span class="qb-mini-value">{{ fmt(ytd.entered) }} m³</span>
              <span class="qb-mini-label">Entered YTD ({{ currentYear }})</span></div>
          </div>
          <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-2">
            <div class="qb-mini"><span class="qb-mini-value">{{ fmt(ytd.exited) }} m³</span>
              <span class="qb-mini-label">Invoiced out YTD</span></div>
          </div>
          <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-2">
            <div class="qb-mini"><span class="qb-mini-value">{{ ytd.net >= 0 ? '+' : '' }}{{ fmt(ytd.net) }} m³</span>
              <span class="qb-mini-label">Net YTD</span></div>
          </div>
          <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-2">
            <div class="qb-mini"><span class="qb-mini-value">{{ troughMonths.join(' · ') }}</span>
              <span class="qb-mini-label">Structural low season</span></div>
          </div>
        </div>

        <!-- 1. The chart the request is about -->
        <h6 class="qb-h">Dashboard volume, month by month — year over year</h6>
        <p class="qb-sub">End-of-month snapshot of the m³ sitting in the quotation dashboard.
          Darker line = more recent year.</p>
        <vue-apex-charts type="line" height="340" :options="yoyChart.options" :series="yoyChart.series" />

        <!-- Same reconstruction, valued in EUR -->
        <h6 class="qb-h mt-6">Dashboard volume, month by month — year over year (EUR)</h6>
        <p class="qb-sub">Same quotations, valued in euros: quantity × unit price over every line,
          discounts deducted.</p>
        <vue-apex-charts type="line" height="340" :options="yoyEurChart.options" :series="yoyEurChart.series" />

        <!-- 2. Seasonality: the answer to "identify the low periods" -->
        <h6 class="qb-h mt-6">Seasonality — structural low months</h6>
        <p class="qb-sub">Average dashboard volume per calendar month across all years.
          The three lowest months are highlighted.</p>
        <vue-apex-charts type="bar" height="280" :options="seasonChart.options" :series="seasonChart.series" />

        <!-- 3. The long view -->
        <h6 class="qb-h mt-6">Continuous trend {{ series[0].year }} → today</h6>
        <p class="qb-sub">Where today sits against the whole history.</p>
        <vue-apex-charts type="area" height="300" :options="trendChart.options" :series="trendChart.series" />

        <!-- 4. Why the backlog moves -->
        <div class="vx-row items-center mt-6 mb-1">
          <div class="vx-col w-full md:w-2/3">
            <h6 class="qb-h mb-0">In vs out per month — {{ flowYear }}</h6>
          </div>
          <div class="vx-col w-full md:w-1/3">
            <v-select :options="yearOptions" v-model="flowYear" :clearable="false" class="qb-select" />
          </div>
        </div>
        <p class="qb-sub">Volume of newly created quotations against volume leaving on a final invoice.</p>
        <vue-apex-charts type="line" height="300" :options="flowChart.options" :series="flowChart.series" />

        <!-- Matrix -->
        <div class="vx-row items-center mt-6 mb-2">
          <div class="vx-col w-full md:w-1/2">
            <h6 class="qb-h mb-0">Year × month table (m³)</h6>
          </div>
          <div class="vx-col w-full md:w-1/2 text-right">
            <vs-button size="small" type="border" class="mr-2"
                       :class="{ 'qb-active': matrixMode === 'backlog' }"
                       @click="matrixMode = 'backlog'">In dashboard</vs-button>
            <vs-button size="small" type="border" class="mr-2"
                       :class="{ 'qb-active': matrixMode === 'entered' }"
                       @click="matrixMode = 'entered'">Entered</vs-button>
            <vs-button size="small" type="border" class="mr-2"
                       :class="{ 'qb-active': matrixMode === 'exited' }"
                       @click="matrixMode = 'exited'">Invoiced out</vs-button>
            <vs-button size="small" icon-pack="feather" icon="icon-download"
                       @click="exportExcel">Excel</vs-button>
          </div>
        </div>

        <div class="qb-matrix-wrap">
          <table class="qb-matrix">
            <thead>
              <tr>
                <th class="qb-matrix-head">Year</th>
                <th v-for="m in monthLabels" :key="m">{{ m }}</th>
                <th class="qb-matrix-sep">Avg</th>
                <th>Min</th>
                <th>Max</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in matrixRows" :key="row.year">
                <th class="qb-matrix-head">{{ row.year }}</th>
                <td v-for="(cell, i) in row.cells" :key="i"
                    :style="cellStyle(cell)"
                    :class="{ 'qb-cell-click': cell !== null }"
                    :title="cell === null ? '' : 'Click for the quotation list'"
                    @click="cell === null ? null : openDetail(row.year, i + 1)">
                  {{ cell === null ? '' : fmt(cell) }}
                </td>
                <td class="qb-matrix-sep qb-matrix-stat">{{ fmt(row.avg) }}</td>
                <td class="qb-matrix-stat">{{ fmt(row.min) }}</td>
                <td class="qb-matrix-stat">{{ fmt(row.max) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th class="qb-matrix-head">Avg</th>
                <td v-for="(v, i) in matrixFooter" :key="i" class="qb-matrix-stat">{{ fmt(v) }}</td>
                <td class="qb-matrix-sep"></td><td></td><td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="qb-legend">
          <span class="qb-legend-label">Low</span>
          <span v-for="c in HEAT_RAMP" :key="c" class="qb-legend-swatch" :style="{ background: c }"></span>
          <span class="qb-legend-label">High</span>
          <span class="qb-legend-note">Values in m³ · click a cell to list the quotations</span>
        </div>

        <!-- Caveats: this page is used to argue with management, so it states its limits. -->
        <div class="qb-caveats mt-6">
          <div class="qb-caveats-head" @click="showCaveats = !showCaveats">
            <feather-icon :icon="showCaveats ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                          svgClasses="h-4 w-4 align-middle mr-1" />
            How this is computed, and what it cannot see
          </div>
          <div v-if="showCaveats" class="qb-caveats-body">
            <p><strong>Rule replayed.</strong> {{ meta.rule }}</p>
            <ul>
              <li><strong>Cancelled or re-pending quotations are invisible in history.</strong>
                The reconstruction uses each quotation's <em>current</em> status and there is no
                status history in the database. A quotation approved in 2021 and cancelled in 2022
                never appears here, so the past backlog is slightly <em>understated</em>.
                Today: {{ q.cancelled_or_pending_count }} such quotations
                ({{ fmt(q.cancelled_or_pending_m3) }} m³), which includes quotations sent back to
                Pending by a merge.</li>
              <li><strong>Entry date = row creation, not approval.</strong> A quotation created in
                November and approved in January is counted from November here, whereas the real
                dashboard only showed it from January.
                <span v-if="q.backdated_quotations">{{ q.backdated_quotations }} quotations have a
                  creation date more than 30 days away from their quotation date.</span></li>
              <li><strong>Split quotations.</strong> {{ q.split_parents }} parent quotations exist.
                They are {{ includeParents ? 'included' : 'excluded' }} — a parent sat in the
                dashboard from its own creation until it was invoiced or split, so excluding them
                erases that volume from history. Use the checkbox above to compare both readings.</li>
              <li v-if="q.invoice_before_quotation"><strong>Back-dated invoices.</strong>
                {{ q.invoice_before_quotation }} quotations have a final invoice created before the
                quotation itself; they are clamped to enter and exit the same day (net zero).</li>
              <li v-if="q.pointer_without_invoice_date"><strong>Broken invoice links.</strong>
                {{ q.pointer_without_invoice_date }} quotations point at a final invoice with no
                usable date; their exit falls back to the last modification date.</li>
              <li v-if="q.approved_without_volume"><strong>Zero-volume quotations.</strong>
                {{ q.approved_without_volume }} approved quotations carry no volume-bearing product
                (services only). They count in the quotation <em>count</em> but contribute 0 m³.</li>
              <li><strong>Product volumes are current, not historical.</strong>
                <code>products.volume</code> has no history, so a volume corrected in 2024 is applied
                retroactively to 2020.</li>
              <li><strong>Sign convention.</strong> This block shows positive m³;
                <code>/api/totalvolumeofquotation</code> returns a negative total that the dashboard
                negates for display. Same number, opposite sign.</li>
            </ul>
          </div>
        </div>
      </div>

      <div v-else key="empty" class="qb-muted text-sm py-4">No data.</div>
      </div>

      <!-- Month drill-down. Kept mounted (never v-if'd) and keyed: Vuesax moves
           it into document.body, so it must always match itself in the diff. -->
      <vs-popup key="qb-detail-popup" :title="detailTitle" :active.sync="detailOpen" fullscreen>
        <div v-if="detailLoading" class="text-center py-8 qb-muted">Loading…</div>
        <template v-else>
          <p class="qb-sub mb-2">
            {{ detail.count }} quotations · {{ fmt(detail.total_m3) }} m³ in the dashboard at the end
            of this month.
          </p>
          <vs-table :data="detail.items" max-items="20" pagination search>
            <template slot="thead">
              <vs-th sort-key="quotation_no">Quotation</vs-th>
              <vs-th sort-key="client">Client</vs-th>
              <vs-th sort-key="m3">m³</vs-th>
              <vs-th sort-key="eur">EUR</vs-th>
              <vs-th sort-key="entry_date">Entered</vs-th>
              <vs-th sort-key="exit_date">Final invoice</vs-th>
              <vs-th sort-key="days_in_dashboard">Days</vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr v-for="(tr, i) in data" :key="i">
                <vs-td>#{{ tr.id }}<span v-if="tr.quotation_no"> / {{ tr.quotation_no }}</span></vs-td>
                <vs-td>{{ tr.client }}</vs-td>
                <vs-td>{{ fmt(tr.m3) }}</vs-td>
                <vs-td>{{ fmt(tr.eur) }}</vs-td>
                <vs-td>{{ tr.entry_date }}</vs-td>
                <vs-td>
                  <span v-if="tr.still_open" class="qb-muted">still in dashboard</span>
                  <span v-else>{{ tr.exit_date }}<span v-if="tr.final_invoice_no"> · {{ tr.final_invoice_no }}</span></span>
                </vs-td>
                <vs-td>{{ tr.days_in_dashboard }}</vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </template>
      </vs-popup>
    </div>
  </vx-card>
</template>

<script>
import axios from "@/axios.js";
import VueApexCharts from "vue-apexcharts";
import vSelect from "vue-select";
import XLSX_STYLE from "xlsx-js-style";

// Years are an ORDINAL scale (old -> recent): one hue, light -> dark, so the
// current year reads as the darkest line. Validated with the dataviz palette
// validator (--ordinal, light, surface #ffffff): all checks pass.
const YEAR_RAMP = ["#A79EF5", "#8E82F2", "#7367F0", "#5A4CD8", "#4638B4", "#342A8A", "#241C60"];
// Sequential fill for the matrix. Same hue, re-stepped so the lightest cell
// still clears the white surface (2.23:1) — validated.
const HEAT_RAMP = ["#ADA4F6", "#8E82F2", "#7367F0", "#5A4CD8", "#4638B4", "#342A8A"];
// In / out are a polarity pair, not good/bad. Validated as a categorical pair
// (CVD dE 24.7 protan, normal-vision 33.6, both >= 3:1 on white).
const FLOW_IN = "#2a78d6";
const FLOW_OUT = "#eb6834";
const NET = "#52514e";   // derived summary series — text ink, not an identity hue
const GRID = "#e6e6e3";
const MUTED = "#898781";
const DEEMPH = "#c9c7c2";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default {
  name: "QuotationBacklogAnalysis",
  components: { VueApexCharts, vSelect },
  data() {
    return {
      HEAT_RAMP,
      monthLabels: MONTHS,
      loading: true,
      errorMessage: null,
      includeParents: true,
      showCaveats: false,
      matrixMode: "backlog",
      flowYear: new Date().getFullYear(),
      meta: {},
      series: [],
      byYear: [],
      seasonality: [],
      aging: [],
      detailOpen: false,
      detailLoading: false,
      detailTitle: "",
      detail: { items: [], count: 0, total_m3: 0 },
    };
  },
  computed: {
    q() {
      return this.meta.quality || {};
    },
    years() {
      return [...new Set(this.series.map((r) => r.year))].sort((a, b) => a - b);
    },
    yearOptions() {
      return [...this.years].sort((a, b) => b - a);
    },
    currentYear() {
      return this.years.length ? this.years[this.years.length - 1] : new Date().getFullYear();
    },
    /** Oldest years fold into gray past the 7 validated ramp steps. */
    yearColors() {
      const n = this.years.length;
      if (n <= YEAR_RAMP.length) return YEAR_RAMP.slice(YEAR_RAMP.length - n);
      return Array(n - YEAR_RAMP.length).fill(DEEMPH).concat(YEAR_RAMP);
    },
    current() {
      return this.series.length ? this.series[this.series.length - 1] : null;
    },
    allTimeAvg() {
      if (!this.series.length) return 0;
      return this.series.reduce((a, r) => a + r.backlog_m3, 0) / this.series.length;
    },
    allTimeMax() {
      return this.series.reduce((a, r) => (a === null || r.backlog_m3 > a.backlog_m3 ? r : a), null) || {};
    },
    allTimeMin() {
      return this.series.reduce((a, r) => (a === null || r.backlog_m3 < a.backlog_m3 ? r : a), null) || {};
    },
    lastYearLabel() {
      if (!this.current) return "";
      return `${MONTHS[this.current.month_no - 1]} ${this.current.year - 1}`;
    },
    /** Same calendar month, previous year — the comparison management asks for. */
    yoyDelta() {
      if (!this.current) return null;
      const prev = this.series.find(
        (r) => r.year === this.current.year - 1 && r.month_no === this.current.month_no
      );
      if (!prev || !prev.backlog_m3) return null;
      return Math.round(((this.current.backlog_m3 - prev.backlog_m3) / prev.backlog_m3) * 100);
    },
    percentileWording() {
      const p = this.meta.percentile;
      if (p === null || p === undefined) return "";
      if (p >= 75) return "Well above the historical median";
      if (p >= 55) return "Above the historical median";
      if (p >= 45) return "Around the historical median";
      if (p >= 25) return "Below the historical median";
      return "Well below the historical median";
    },
    ytd() {
      const rows = this.series.filter((r) => r.year === this.currentYear);
      const entered = rows.reduce((a, r) => a + r.entered_m3, 0);
      const exited = rows.reduce((a, r) => a + r.exited_m3, 0);
      return { entered, exited, net: entered - exited };
    },
    troughMonths() {
      return [...this.seasonality]
        .sort((a, b) => a.avg_backlog_m3 - b.avg_backlog_m3)
        .slice(0, 3)
        .map((s) => s.label);
    },
    currentCohort() {
      return this.aging.find((a) => a.cohort_year === this.currentYear) || {};
    },
    previousCohort() {
      return this.aging.find((a) => a.cohort_year === this.currentYear - 1) || {};
    },

    // --- Chart 1: year over year, same reconstruction in m³ then in EUR ----
    yoyChart() {
      return this.makeYoyChart("backlog_m3", "m³");
    },
    yoyEurChart() {
      return this.makeYoyChart("backlog_eur", "€");
    },

    // --- Chart 2: seasonality --------------------------------------------
    seasonChart() {
      const values = this.seasonality.map((s) => Math.round(s.avg_backlog_m3));
      const sorted = [...values].sort((a, b) => a - b);
      const cutoff = sorted[2];
      // One accent + gray context: the three low months are the point.
      const colors = values.map((v) => (v <= cutoff ? FLOW_IN : DEEMPH));
      const mean = values.reduce((a, v) => a + v, 0) / (values.length || 1);
      return {
        series: [{ name: "Average m³ in dashboard", data: values }],
        options: {
          chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
          colors,
          plotOptions: {
            bar: {
              columnWidth: "55%", borderRadius: 4, borderRadiusApplication: "end",
              distributed: true,
              dataLabels: { position: "top" },   // above the cap, not inside the fill
            },
          },
          // Direct labels only on the three highlighted months, not on every bar.
          dataLabels: {
            enabled: true,
            formatter: (v, o) => (values[o.dataPointIndex] <= cutoff ? this.fmt(v) : ""),
            offsetY: -20, style: { colors: ["#52514e"], fontSize: "11px", fontWeight: 600 },
          },
          xaxis: {
            categories: MONTHS,
            axisBorder: { color: GRID }, axisTicks: { color: GRID },
            labels: { style: { colors: MUTED } },
          },
          yaxis: { labels: { style: { colors: MUTED }, formatter: (v) => this.fmt(v) } },
          grid: { borderColor: GRID, strokeDashArray: 0 },
          legend: { show: false },
          tooltip: { y: { formatter: (v) => this.fmt(v) + " m³" } },
          annotations: {
            yaxis: [{
              y: mean, borderColor: MUTED, strokeDashArray: 4,
              label: {
                text: "12-month average " + this.fmt(mean) + " m³",
                position: "left", textAnchor: "start",
                style: { color: MUTED, background: "#fff", fontSize: "11px" },
              },
            }],
          },
        },
      };
    },

    // --- Chart 3: continuous trend ---------------------------------------
    trendChart() {
      const hi = this.allTimeMax;
      const lo = this.allTimeMin;
      const points = [hi, lo].filter((p) => p && p.month).map((p) => ({
        x: p.month, y: Math.round(p.backlog_m3),
        marker: { size: 5, fillColor: "#fff", strokeColor: FLOW_IN, strokeWidth: 2 },
        label: {
          text: (p === hi ? "High " : "Low ") + this.fmt(p.backlog_m3) + " m³",
          borderColor: GRID,
          style: { color: MUTED, background: "#fff", fontSize: "11px" },
        },
      }));
      return {
        // Single series: the title names it, so no legend box.
        series: [{ name: "m³ in dashboard", data: this.series.map((r) => Math.round(r.backlog_m3)) }],
        options: {
          chart: { toolbar: { show: false }, fontFamily: "inherit", zoom: { enabled: false } },
          colors: [FLOW_IN],
          stroke: { width: 2, curve: "straight" },
          fill: {
            type: "gradient",
            gradient: { opacityFrom: 0.25, opacityTo: 0.02, shadeIntensity: 1, stops: [0, 90, 100] },
          },
          dataLabels: { enabled: false },
          markers: { size: 0, strokeWidth: 2, strokeColors: "#fff", hover: { size: 5 } },
          xaxis: {
            categories: this.series.map((r) => r.month),
            tickAmount: 12,
            axisBorder: { color: GRID }, axisTicks: { color: GRID },
            labels: { style: { colors: MUTED }, rotate: -45, rotateAlways: false },
          },
          yaxis: { min: 0, labels: { style: { colors: MUTED }, formatter: (v) => this.fmt(v) } },
          grid: { borderColor: GRID, strokeDashArray: 0 },
          legend: { show: false },
          tooltip: { x: { show: true }, y: { formatter: (v) => this.fmt(v) + " m³" } },
          annotations: {
            points,
            yaxis: [{
              y: this.allTimeAvg, borderColor: MUTED, strokeDashArray: 4,
              label: {
                text: "Average " + this.fmt(this.allTimeAvg) + " m³",
                position: "left", textAnchor: "start",
                style: { color: MUTED, background: "#fff", fontSize: "11px" },
              },
            }],
          },
        },
      };
    },

    // --- Chart 4: in vs out ----------------------------------------------
    flowChart() {
      const rows = this.series.filter((r) => r.year === this.flowYear);
      return {
        series: [
          { name: "Entered", type: "column", data: rows.map((r) => Math.round(r.entered_m3)) },
          // Plotted negative: it is the opposite direction, on the same m³ axis.
          { name: "Invoiced out", type: "column", data: rows.map((r) => -Math.round(r.exited_m3)) },
          { name: "Net", type: "line", data: rows.map((r) => Math.round(r.entered_m3 - r.exited_m3)) },
        ],
        options: {
          chart: { stacked: false, toolbar: { show: false }, fontFamily: "inherit" },
          colors: [FLOW_IN, FLOW_OUT, NET],
          plotOptions: { bar: { columnWidth: "55%", borderRadius: 4, borderRadiusApplication: "end" } },
          stroke: { width: [0, 0, 2], curve: "straight" },
          markers: { size: 0, strokeWidth: 2, strokeColors: "#fff", hover: { size: 5 } },
          dataLabels: { enabled: false },
          xaxis: {
            categories: rows.map((r) => MONTHS[r.month_no - 1]),
            axisBorder: { color: GRID }, axisTicks: { color: GRID },
            labels: { style: { colors: MUTED } },
          },
          yaxis: {
            labels: { style: { colors: MUTED }, formatter: (v) => this.fmt(Math.abs(v)) },
            title: { text: "m³", style: { color: MUTED, fontWeight: 500 } },
          },
          grid: { borderColor: GRID, strokeDashArray: 0 },
          legend: { position: "bottom", horizontalAlign: "center", markers: { radius: 2 } },
          tooltip: {
            shared: true, intersect: false,
            y: { formatter: (v) => this.fmt(Math.abs(v)) + " m³" },
          },
        },
      };
    },

    // --- Matrix ------------------------------------------------------------
    matrixKey() {
      return this.matrixMode === "backlog" ? "backlog_m3"
        : this.matrixMode === "entered" ? "entered_m3" : "exited_m3";
    },
    matrixRows() {
      const key = this.matrixKey;
      return this.years.map((y) => {
        const cells = Array.from({ length: 12 }, (_, i) => {
          const r = this.series.find((s) => s.year === y && s.month_no === i + 1);
          return r ? r[key] : null;
        });
        const present = cells.filter((c) => c !== null);
        return {
          year: y,
          cells,
          avg: present.length ? present.reduce((a, v) => a + v, 0) / present.length : 0,
          min: present.length ? Math.min(...present) : 0,
          max: present.length ? Math.max(...present) : 0,
        };
      });
    },
    matrixFooter() {
      const key = this.matrixKey;
      return Array.from({ length: 12 }, (_, i) => {
        const vals = this.series.filter((s) => s.month_no === i + 1).map((s) => s[key]);
        return vals.length ? vals.reduce((a, v) => a + v, 0) / vals.length : 0;
      });
    },
    matrixBounds() {
      const vals = this.matrixRows.flatMap((r) => r.cells).filter((c) => c !== null);
      if (!vals.length) return { min: 0, max: 1 };
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      return { min, max: max === min ? min + 1 : max };
    },
  },
  watch: {
    includeParents() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmt(v) {
      const n = Number(v);
      if (!isFinite(n)) return "–";
      return Math.round(n).toLocaleString("en-US").replace(/,/g, " ");
    },
    /** Like fmt but keeps a real 0 (a median of 0 days is a fact, not a gap). */
    num(v) {
      return v === null || v === undefined || v === "" ? "–" : v;
    },
    /**
     * The year-over-year chart, driven by one series key. Used twice: once for
     * m³ and once for EUR. Same reconstruction, same ordinal year ramp — only
     * the unit changes, so the two charts read as one pair.
     */
    makeYoyChart(key, unit) {
      const cur = this.currentYear;
      const avg = this.series.length
        ? this.series.reduce((a, r) => a + (Number(r[key]) || 0), 0) / this.series.length
        : 0;
      return {
        series: this.years.map((y) => ({
          name: String(y),
          data: Array.from({ length: 12 }, (_, i) => {
            const r = this.series.find((s) => s.year === y && s.month_no === i + 1);
            return r ? Math.round(Number(r[key]) || 0) : null;
          }),
        })),
        options: {
          chart: { toolbar: { show: false }, fontFamily: "inherit", zoom: { enabled: false } },
          colors: this.yearColors,
          // Current year thicker so it reads first among six lines.
          stroke: { width: this.years.map((y) => (y === cur ? 3 : 2)), curve: "straight", lineCap: "round" },
          // 2px surface ring so markers stay legible where lines cross.
          markers: { size: 0, strokeWidth: 2, strokeColors: "#fff", hover: { size: 5 } },
          dataLabels: { enabled: false },
          xaxis: {
            categories: MONTHS,
            axisBorder: { color: GRID }, axisTicks: { color: GRID },
            labels: { style: { colors: MUTED } },
          },
          yaxis: {
            min: 0,
            labels: { style: { colors: MUTED }, formatter: (v) => this.fmt(v) },
            title: { text: unit + " in dashboard", style: { color: MUTED, fontWeight: 500 } },
          },
          grid: { borderColor: GRID, strokeDashArray: 0 },
          legend: { position: "bottom", horizontalAlign: "center", markers: { radius: 2 } },
          tooltip: {
            shared: true, intersect: false,
            y: { formatter: (v) => (v == null ? "–" : this.fmt(v) + " " + unit) },
          },
          annotations: {
            yaxis: [{
              y: avg, borderColor: MUTED, strokeDashArray: 4,
              label: {
                text: "All-period average " + this.fmt(avg) + " " + unit,
                position: "left", textAnchor: "start",
                style: { color: MUTED, background: "#fff", fontSize: "11px" },
              },
            }],
          },
        },
      };
    },
    async load(force) {
      this.loading = true;
      this.errorMessage = null;
      try {
        const { data } = await axios.get("/analytics/quotation-backlog/monthly", {
          // No `from`: the API starts the series at the first month with data.
          params: {
            include_parents: this.includeParents ? 1 : 0,
            nocache: force === true ? 1 : undefined,
          },
        });
        this.meta = data.meta || {};
        this.series = data.series || [];
        this.byYear = data.by_year || [];
        this.seasonality = data.seasonality || [];
        this.aging = data.aging || [];
        if (!this.years.includes(this.flowYear) && this.years.length) {
          this.flowYear = this.years[this.years.length - 1];
        }
      } catch (err) {
        this.errorMessage =
          (err.response && err.response.data && err.response.data.message) ||
          "Unable to load the quotation backlog history.";
      } finally {
        this.loading = false;
      }
    },
    /** Sequential fill: 6 steps of one hue, light -> dark. */
    cellStyle(value) {
      if (value === null) return {};
      const { min, max } = this.matrixBounds;
      const t = (value - min) / (max - min);
      const idx = Math.min(HEAT_RAMP.length - 1, Math.max(0, Math.round(t * (HEAT_RAMP.length - 1))));
      return {
        background: HEAT_RAMP[idx],
        // Label inside a colored fill: flip the ink on the dark half.
        color: idx >= 2 ? "#ffffff" : "#0b0b0b",
      };
    },
    async openDetail(year, monthNo) {
      const month = `${year}-${String(monthNo).padStart(2, "0")}`;
      this.detailTitle = `Quotations in the dashboard — end of ${MONTHS[monthNo - 1]} ${year}`;
      this.detailOpen = true;
      this.detailLoading = true;
      try {
        const { data } = await axios.get("/analytics/quotation-backlog/detail", {
          params: { month, include_parents: this.includeParents ? 1 : 0 },
        });
        this.detail = data;
      } catch (err) {
        this.detail = { items: [], count: 0, total_m3: 0 };
        this.$vs.notify({
          title: "Error", text: "Unable to load the quotations for this month.",
          iconPack: "feather", icon: "icon-alert-circle", color: "danger",
          position: "bottom-right", time: 6000,
        });
      } finally {
        this.detailLoading = false;
      }
    },
    exportExcel() {
      const header = ["Year", ...MONTHS, "Avg", "Min", "Max"];
      const rows = this.matrixRows.map((r) => [
        r.year,
        ...r.cells.map((c) => (c === null ? "" : Math.round(c))),
        Math.round(r.avg), Math.round(r.min), Math.round(r.max),
      ]);
      const matrix = XLSX_STYLE.utils.aoa_to_sheet([
        [`Quotation dashboard volume (m3) — ${this.matrixMode}`],
        [this.meta.rule],
        [],
        header,
        ...rows,
      ]);
      const detail = XLSX_STYLE.utils.json_to_sheet(
        this.series.map((r) => ({
          Month: r.month,
          "In dashboard (m3)": Math.round(r.backlog_m3),
          "Quotations": r.backlog_count,
          "Entered (m3)": Math.round(r.entered_m3),
          "Entered (count)": r.entered_count,
          "Invoiced out (m3)": Math.round(r.exited_m3),
          "Invoiced out (count)": r.exited_count,
        }))
      );
      const wb = XLSX_STYLE.utils.book_new();
      XLSX_STYLE.utils.book_append_sheet(wb, matrix, "Year x Month");
      XLSX_STYLE.utils.book_append_sheet(wb, detail, "Monthly series");
      XLSX_STYLE.writeFile(wb, `quotation-dashboard-volume-${this.meta.to || ""}.xlsx`);
    },
  },
};
</script>

<style scoped>
.qb-muted { color: #898781; }
.qb-h { font-size: 0.95rem; font-weight: 600; margin-bottom: 2px; }
.qb-sub { font-size: 0.75rem; color: #898781; margin-bottom: 10px; }
.qb-unit { font-size: 0.75rem; font-weight: 500; color: #898781; }

.qb-parity {
  font-size: 0.78rem; padding: 8px 12px; border-radius: 6px; display: flex; align-items: center;
}
.qb-parity-ok { background: #f2f7ff; color: #1f5fa8; }
.qb-parity-ko { background: #fdf1ec; color: #a8421c; }

.qb-hero {
  padding: 14px 16px; border-radius: 8px; background: #f3f2fd; border-left: 3px solid #7367F0;
}
.qb-hero-value { font-size: 1.9rem; font-weight: 700; line-height: 1.1; color: #241C60; }
.qb-hero-label { font-size: 0.78rem; color: #52514e; margin-top: 2px; }
.qb-hero-sub { font-size: 0.72rem; color: #898781; margin-top: 4px; }

.qb-tile { padding: 14px 16px; border-radius: 8px; background: #f8f9fb; height: 100%; }
.qb-tile-value { font-size: 1.35rem; font-weight: 700; line-height: 1.1; }
.qb-tile-label { font-size: 0.78rem; color: #52514e; margin-top: 2px; }
.qb-tile-sub { font-size: 0.7rem; color: #898781; margin-top: 4px; }

.qb-mini {
  padding: 10px 12px; border-radius: 6px; background: #f8f9fb;
  display: flex; flex-direction: column;
}
.qb-mini-value { font-size: 0.95rem; font-weight: 600; }
.qb-mini-label { font-size: 0.7rem; color: #898781; }

.qb-chip {
  display: inline-block; padding: 1px 7px; border-radius: 10px; font-size: 0.68rem;
  font-weight: 600; margin-left: 6px;
}
.qb-chip-up { background: #e6f4ed; color: #12684a; }
.qb-chip-down { background: #fdf1ec; color: #a8421c; }

.qb-select { font-size: 0.8rem; }
.qb-active { font-weight: 700; }

.qb-matrix-wrap { overflow-x: auto; }
.qb-matrix { width: 100%; border-collapse: separate; border-spacing: 2px; font-size: 0.72rem; }
.qb-matrix th {
  font-weight: 600; color: #52514e; text-align: center; padding: 4px 6px; white-space: nowrap;
}
.qb-matrix td {
  text-align: right; padding: 5px 8px; border-radius: 3px; white-space: nowrap;
  min-width: 52px; background: #f6f6f4; color: #898781;
}
.qb-matrix-head { text-align: left !important; }
.qb-matrix-sep { border-left: 2px solid #fff; }
.qb-matrix-stat { background: transparent !important; color: #52514e !important; font-weight: 600; }
.qb-cell-click { cursor: pointer; }
.qb-cell-click:hover { outline: 2px solid #241C60; }

.qb-legend { display: flex; align-items: center; gap: 4px; margin-top: 8px; font-size: 0.7rem; color: #898781; }
.qb-legend-swatch { width: 22px; height: 10px; border-radius: 2px; }
.qb-legend-label { margin: 0 4px; }
.qb-legend-note { margin-left: 12px; }

.qb-caveats { border-top: 1px solid #e6e6e3; padding-top: 10px; }
.qb-caveats-head { font-size: 0.78rem; font-weight: 600; color: #52514e; cursor: pointer; }
.qb-caveats-body { font-size: 0.72rem; color: #52514e; margin-top: 8px; line-height: 1.55; }
.qb-caveats-body ul { list-style: disc; padding-left: 18px; margin-top: 6px; }
.qb-caveats-body li { margin-bottom: 5px; }
.qb-caveats-body code { background: #f3f2fd; padding: 0 3px; border-radius: 2px; }
</style>
